import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { type TemplateType } from '@cls-nordic/pdf-templates/browser';

import { useCurrentUser } from '@/store/currentUser';
import { useLearningJourney } from '@/store/v2/journeys/useLearningJourney';
import { RCertificateTemplateType, useCertificatePropertiesQueryRemote } from '@/store/v2';
import { getJourneyCertificateUrn } from '@/store/v2/journeys/certificate';
import { getPdfTemplateId } from '@/features/journey/admin/utils/getPdfTemplateId';
import {
  CertificatePreviewInput,
  certificatePropertiesToPreviewInput,
} from '@/pages/user/JourneyCertificate/certificatePreviewInput';

type UseJourneyCertificateResult = {
  /** PDF preview input. Null until certificate properties have loaded. */
  payload: CertificatePreviewInput | null;
  /** pdf-templates template id matching the stored template type. */
  templateId: TemplateType;
  /**
   * True while any source feeding `payload` is still loading. Combining the flags prevents the
   * preview from rendering with a placeholder name/title before user & journey data resolve.
   */
  loading: boolean;
  /**
   * True once the journey query has actually resolved and yielded no certificate URL — i.e. the
   * learner isn't entitled to a certificate. Driven by the query's `called` flag (not loading
   * booleans) so it never flips true during the skipped/resolving window.
   */
  notFound: boolean;
  error?: ApolloError;
  /** Backend-generated presigned URL for downloading the certificate. */
  certificateUrl?: string;
};

/** Aggregates everything the certificate page needs: the PDF preview payload and the download URL. */
export const useJourneyCertificate = (journeyId: number): UseJourneyCertificateResult => {
  const urn = getJourneyCertificateUrn(journeyId);

  const { user } = useCurrentUser();
  const {
    journey,
    loading: journeyLoading,
    called: journeyCalled,
  } = useLearningJourney({
    id: journeyId,
    skip: !journeyId,
  });
  const {
    data,
    loading: certificateLoading,
    error,
  } = useCertificatePropertiesQueryRemote({
    variables: { urn },
    skip: !journeyId,
  });

  const learnerName = [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.username || 'Learner';
  const certificateProperties = data?.certificateProperties;
  const journeyTitle = journey?.title;

  const payload = useMemo<CertificatePreviewInput | null>(
    () => certificatePropertiesToPreviewInput({ certificateProperties, learnerName, journeyTitle }),
    [certificateProperties, learnerName, journeyTitle],
  );

  const templateId = getPdfTemplateId(certificateProperties?.templateType ?? RCertificateTemplateType.Professional);

  const certificateUrl = journey?.myCertificateURL?.url;

  // useLearningJourney skips its query (called === false) until spaces, the user, and a userId are
  // ready, then tracks its own loading. So the journey is "resolved" only once it has genuinely run
  // and finished — relying on `called` (not sibling loading flags, which read false for a render
  // before the query starts) is what stops the intermittent /404 on a cold load (refresh / new tab).
  const journeyResolved = journeyCalled && !journeyLoading;

  return {
    payload,
    templateId,
    loading: certificateLoading || !journeyResolved,
    // No certificate URL once the journey has resolved => the learner isn't entitled to a certificate.
    notFound: journeyResolved && !certificateUrl,
    error,
    certificateUrl,
  };
};
